import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut, Move, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Info, X } from 'lucide-react';

const TUTORIAL_KEY = 'copa_coruja_crop_tutorial_seen';

export default function ImageCropModal({ isOpen, onClose, onSave, imageUrl }) {
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(0.5);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showTutorial, setShowTutorial] = useState(false);
  const prevScaleRef = useRef(0.5);

  // Check if first time user
  useEffect(() => {
    if (isOpen) {
      const tutorialSeen = localStorage.getItem(TUTORIAL_KEY);
      if (!tutorialSeen) {
        setShowTutorial(true);
      }
    }
  }, [isOpen]);

  const closeTutorial = () => {
    localStorage.setItem(TUTORIAL_KEY, 'true');
    setShowTutorial(false);
  };

  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        const canvas = canvasRef.current;
        if (canvas) {
          // Calculate initial scale to fit image in canvas
          const canvasSize = canvas.width;
          const imgMaxDim = Math.max(img.width, img.height);
          const initialScale = Math.min(1, (canvasSize * 0.8) / imgMaxDim);
          
          // Center image
          const centerX = (canvasSize - img.width * initialScale) / 2;
          const centerY = (canvasSize - img.height * initialScale) / 2;
          
          setPosition({ x: centerX, y: centerY });
          setScale(initialScale);
          prevScaleRef.current = initialScale;
        }
      };
      img.src = imageUrl;
    }
  }, [imageUrl]);

  useEffect(() => {
    if (image && canvasRef.current) {
      drawCanvas();
    }
  }, [image, scale, position]);

  const handleScaleChange = (newScale) => {
    if (!image || !canvasRef.current) {
      setScale(newScale);
      return;
    }

    const canvas = canvasRef.current;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const imageCenterX = position.x + (image.width * prevScaleRef.current) / 2;
    const imageCenterY = position.y + (image.height * prevScaleRef.current) / 2;

    const offsetX = imageCenterX - centerX;
    const offsetY = imageCenterY - centerY;

    const scaleDiff = newScale / prevScaleRef.current;
    const newX = centerX + offsetX * scaleDiff - (image.width * newScale) / 2;
    const newY = centerY + offsetY * scaleDiff - (image.height * newScale) / 2;

    setPosition({ x: newX, y: newY });
    setScale(newScale);
    prevScaleRef.current = newScale;
  };

  // Fine movement controls - inverted because moving the image position 
  // in positive direction makes it appear to move in that direction visually
  const moveImage = (dx, dy) => {
    const step = 10; // pixels per step
    setPosition(prev => ({
      x: prev.x - dx * step,
      y: prev.y - dy * step
    }));
  };

  // Zoom controls - 1% increments
  const zoomIn = () => {
    const newScale = Math.min(scale + 0.01, 4);
    handleScaleChange(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(scale - 0.01, 0.1);
    handleScaleChange(newScale);
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.clip();
    
    ctx.translate(position.x, position.y);
    ctx.scale(scale, scale);
    ctx.drawImage(image, 0, 0);
    
    ctx.restore();
    
    // Draw circle border
    ctx.strokeStyle = '#FF5A00';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius - 2, 0, Math.PI * 2);
    ctx.stroke();

    // Draw guide lines (cross)
    ctx.strokeStyle = 'rgba(255, 90, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    
    // Horizontal line
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(canvas.width, centerY);
    ctx.stroke();
    
    // Vertical line
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, canvas.height);
    ctx.stroke();
    
    ctx.setLineDash([]);
  };

  const getEventPosition = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  const handleStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const pos = getEventPosition(e);
    setDragStart({
      x: pos.x - position.x,
      y: pos.y - position.y
    });
  };

  const handleMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const pos = getEventPosition(e);
    setPosition({
      x: pos.x - dragStart.x,
      y: pos.y - dragStart.y
    });
  };

  const handleEnd = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2;
    
    // Redraw canvas WITHOUT guide lines for saving
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    
    // Clip to circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.clip();
    
    // Draw the image
    ctx.translate(position.x, position.y);
    ctx.scale(scale, scale);
    ctx.drawImage(image, 0, 0);
    
    ctx.restore();
    
    // Export WITHOUT border and guide lines
    const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
    
    // Redraw with guides for continued editing if user cancels
    drawCanvas();
    
    onSave(croppedDataUrl);
  };

  // Tutorial overlay component
  const TutorialOverlay = () => (
    <div 
      className="absolute inset-0 bg-black/95 flex items-center justify-center p-4 rounded-lg" 
      style={{ zIndex: 100 }}
      onClick={closeTutorial}
    >
      <div className="bg-[#14151C] border-2 border-[#FF5A00] rounded-2xl max-w-sm w-full p-5 relative" onClick={e => e.stopPropagation()}>
        <button 
          onClick={closeTutorial}
          className="absolute top-3 right-3 text-white/60 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="text-center mb-4">
          <div className="w-14 h-14 bg-[#FF5A00]/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Info className="h-7 w-7 text-[#FF5A00]" />
          </div>
          <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>
            Como Ajustar sua Foto
          </h3>
          <p className="text-[#A1A1AA] text-xs">
            Centralize seu rosto dentro do círculo
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-white/5 rounded-lg p-2">
            <div className="w-8 h-8 bg-[#FF5A00]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Move className="h-4 w-4 text-[#FF5A00]" />
            </div>
            <div>
              <p className="text-white font-semibold text-xs">Mover a Foto</p>
              <p className="text-[#A1A1AA] text-[10px]">
                Arraste com o dedo ou use as setas
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-white/5 rounded-lg p-2">
            <div className="w-8 h-8 bg-[#FF5A00]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <ZoomIn className="h-4 w-4 text-[#FF5A00]" />
            </div>
            <div>
              <p className="text-white font-semibold text-xs">Ajustar o Zoom</p>
              <p className="text-[#A1A1AA] text-[10px]">
                Use o slider ou os botões + e -
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-white/5 rounded-lg p-2">
            <div className="w-8 h-8 bg-[#FF5A00]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-[#FF5A00] text-sm font-bold">+</span>
            </div>
            <div>
              <p className="text-white font-semibold text-xs">Linhas Guia</p>
              <p className="text-[#A1A1AA] text-[10px]">
                Use as linhas para centralizar o rosto
              </p>
            </div>
          </div>
        </div>

        <Button 
          onClick={closeTutorial}
          className="w-full mt-4 btn-primary text-sm py-2"
        >
          Entendi!
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="glass-card border-white/10 text-white max-w-md mx-4" data-testid="image-crop-modal">
          {showTutorial && <TutorialOverlay />}
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              Ajustar Foto
              <button 
                onClick={() => setShowTutorial(true)}
                className="text-[#FF5A00] hover:text-[#FF5A00]/80 transition-colors"
                title="Ver tutorial"
              >
                <Info className="h-5 w-5" />
              </button>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Canvas area */}
            <div className="flex items-center justify-center">
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  width={280}
                  height={280}
                  className="rounded-full border-2 border-[#FF5A00] touch-none"
                  style={{ 
                    background: '#14151C',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    touchAction: 'none'
                  }}
                  onMouseDown={handleStart}
                  onMouseMove={handleMove}
                  onMouseUp={handleEnd}
                  onMouseLeave={handleEnd}
                  onTouchStart={handleStart}
                  onTouchMove={handleMove}
                  onTouchEnd={handleEnd}
                  data-testid="crop-canvas"
                />
              </div>
            </div>

            {/* Direction controls for mobile */}
            <div className="flex flex-col items-center gap-1">
              <p className="text-xs text-[#A1A1AA] mb-1">Ajuste Fino de Posição</p>
              <div className="grid grid-cols-3 gap-1 w-32">
                <div></div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => moveImage(0, 1)}
                  className="bg-white/5 border-white/20 hover:bg-[#FF5A00]/20 hover:border-[#FF5A00] h-10 w-10 p-0"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <div></div>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => moveImage(1, 0)}
                  className="bg-white/5 border-white/20 hover:bg-[#FF5A00]/20 hover:border-[#FF5A00] h-10 w-10 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="h-10 w-10 flex items-center justify-center">
                  <Move className="h-4 w-4 text-[#A1A1AA]" />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => moveImage(-1, 0)}
                  className="bg-white/5 border-white/20 hover:bg-[#FF5A00]/20 hover:border-[#FF5A00] h-10 w-10 p-0"
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
                
                <div></div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => moveImage(0, -1)}
                  className="bg-white/5 border-white/20 hover:bg-[#FF5A00]/20 hover:border-[#FF5A00] h-10 w-10 p-0"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <div></div>
              </div>
            </div>

            {/* Zoom controls */}
            <div className="space-y-2 px-2">
              <div className="flex items-center justify-between text-white text-sm">
                <span className="flex items-center gap-1">
                  <ZoomOut className="h-4 w-4" />
                  <span className="text-xs">Zoom</span>
                </span>
                <span className="font-bold text-[#FF5A00]">
                  {Math.round(scale * 100)}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-xs">Zoom</span>
                  <ZoomIn className="h-4 w-4" />
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={zoomOut}
                  disabled={scale <= 0.1}
                  className="bg-white/5 border-white/20 hover:bg-[#FF5A00]/20 hover:border-[#FF5A00] h-10 w-10 p-0 flex-shrink-0"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                
                <Slider
                  value={[scale]}
                  onValueChange={(values) => handleScaleChange(values[0])}
                  min={0.1}
                  max={3}
                  step={0.01}
                  className="flex-1"
                  data-testid="zoom-slider"
                />
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={zoomIn}
                  disabled={scale >= 3}
                  className="bg-white/5 border-white/20 hover:bg-[#FF5A00]/20 hover:border-[#FF5A00] h-10 w-10 p-0 flex-shrink-0"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Quick tip */}
            <div className="bg-[#FF5A00]/10 border border-[#FF5A00]/30 rounded-lg p-2 mx-2">
              <p className="text-[#A1A1AA] text-xs text-center">
                <strong className="text-white">Dica:</strong> Centralize seu rosto usando as linhas guia
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={onClose} className="btn-secondary flex-1 sm:flex-none" data-testid="cancel-crop-button">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="btn-primary flex-1 sm:flex-none" data-testid="save-crop-button">
              Aplicar Foto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
